import React, { useEffect, useState } from "react";
import { useClients } from "@/context/ClientContext";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { PlatformBadge } from "@/components/Badges";
import { toast } from "sonner";
import { MessageSquare, AlertOctagon, Eye, Reply, RefreshCw } from "lucide-react";

const SENT = { positiv: "text-emerald-600", neutral: "text-slate-500", negativ: "text-rose-600" };
const ESC = {
  beobachten: { label: "Beobachten", cls: "bg-slate-100 text-slate-600", icon: Eye },
  beantworten: { label: "Beantworten", cls: "bg-blue-50 text-blue-700", icon: Reply },
  sofort_melden: { label: "Sofort melden", cls: "bg-red-100 text-red-700", icon: AlertOctagon },
};

export default function Kommentare() {
  const { user } = useAuth();
  const readOnly = Boolean(user?.review_access?.read_only);
  const { current } = useClients();
  const [comments, setComments] = useState([]);
  const [filter, setFilter] = useState("");
  const [syncing, setSyncing] = useState(false);

  const load = () => {
    if (!current) return;
    const p = new URLSearchParams({ client_id: current.id });
    if (filter) p.append("escalation", filter);
    api.get(`/comments?${p.toString()}`).then(({ data }) => setComments(data));
  };
  useEffect(() => { load(); }, [current, filter]);

  const update = async (id, patch) => { try { await api.patch(`/comments/${id}`, patch); toast.success("Aktualisiert"); load(); } catch (e) { toast.error(apiError(e)); } };
  const syncComments = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/social/sync-comments", { client_id: current.id });
      const failed = (data.results || []).filter((row) => row.status === "error");
      toast.success(`${data.created || 0} neue und ${data.updated || 0} vorhandene Kommentare synchronisiert.`);
      if (failed.length) toast.warning(`${failed.length} Plattform(en) konnten nicht synchronisiert werden.`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSyncing(false);
    }
  };

  if (!current) return <div className="text-slate-400">Kein Mandant ausgewählt.</div>;

  return (
    <div>
      <PageHeader overline={current.name} title="Inbox / Kommentar-Monitoring" subtitle="Zentrale Ansicht aller Kommentare mit Sentiment, Eskalation & Antwortvorschlägen. Keine Auto-Antwort ohne Freigabe." />
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex gap-2 flex-wrap">
          {["", "beobachten", "beantworten", "sofort_melden"].map((f) => (
            <button key={f} data-testid={`comment-filter-${f || "all"}`} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border ${filter === f ? "bg-[#0A0A0A] text-white border-black" : "bg-white border-slate-200 text-slate-500"}`}>
              {f === "" ? "Alle" : ESC[f].label}
            </button>
          ))}
        </div>
        <Button variant="outline" onClick={syncComments} disabled={syncing || readOnly} data-testid="sync-comments-btn" className="rounded-full shrink-0">
          <RefreshCw size={14} className={`mr-2 ${syncing ? "animate-spin" : ""}`} />
          Kommentare synchronisieren
        </Button>
      </div>

      {comments.length === 0 ? (
        <EmptyState icon={MessageSquare} title="Keine Kommentare" hint="Kommentare werden abgerufen, sobald Plattform-APIs verbunden sind." />
      ) : (
        <div className="space-y-3">
          {comments.map((c) => {
            const e = ESC[c.escalation] || ESC.beobachten;
            const EIcon = e.icon;
            return (
              <Card key={c.id} data-testid={`comment-${c.id}`} className="p-4 rounded-md border-slate-200 shadow-none">
                <div className="flex items-start gap-3">
                  <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-semibold shrink-0">{c.author?.slice(0, 2).toUpperCase()}</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm">{c.author}</span>
                      <PlatformBadge platform={c.platform} />
                      <span className={`text-xs font-medium ${SENT[c.sentiment]}`}>● {c.sentiment}</span>
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-sm flex items-center gap-1 ${e.cls}`}><EIcon size={11} />{e.label}</span>
                    </div>
                    <p className="text-sm text-slate-700 mt-1.5">{c.text}</p>
                    <div className="flex items-center gap-2 mt-3">
                      {["beobachten", "beantworten", "sofort_melden"].map((es) => (
                        <button key={es} disabled={readOnly} data-testid={`comment-esc-${c.id}-${es}`} onClick={() => update(c.id, { escalation: es })}
                          className={`text-xs px-2.5 py-1 rounded-full border ${c.escalation === es ? "bg-[#0A0A0A] text-white border-black" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>{ESC[es].label}</button>
                      ))}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
